<template>
  <div ref="filterContainer" :class="{'cursor-wait' : isLoading}" class="w-full">
    <!-- Filter Component -->

    <base-card>
      <PMAFilters
        @change-referral-filters="setApprovalFilters"
        @set-changed-referral-filters="setChangedApprovalsFilters"
        :companyOptions="companyTypeAheadOptions"
        :collectionTagOptions="collectionTagOptions"
        :campaignOptions="campaignOptions"
        :visibleObject="isDropDownVisible"
      ></PMAFilters>
    </base-card>

    <base-card>
      <div class="text-xs text-gray-700 mb-1 px-1.5">
        <template v-if="selectedCompanies.length > 0">
          {{ selectedGroupNames.length }} group{{ selectedGroupNames.length === 1 ? '' : 's' }} selected ·
          {{ selectedCompanies.length }} unique linked Compan{{ selectedCompanies.length === 1 ? 'y' : 'ies' }}
          <button @click="clearSelectedCompanies" class="ml-2 underline text-indigo-600 hover:text-indigo-800">Clear</button>
        </template>
        <span v-else class="text-gray-400 italic">Select groups below to enable bulk operations</span>
      </div>
      <PMABulkOperations
        :selectedCompanies="selectedCompanies"
        :campaigns="campaigns"
        :collection-tags="collectionTags"
        @refetch-companies="loadCompanies(fetchFilter, true)"
        @load-campaigns="loadCampaigns"
        @load-collection-tags="loadCollectionTags"
        @clear-selected-companies="clearSelectedCompanies"
        @reset-select-all="clearSelectedCompanies"
      />
    </base-card>

    <!-- Header Component -->

    <base-card>
      <base-pagination-header
        @load-data="loadCompanies(fetchFilter, true)"
        @get-next="getNext"
        @go-to-first="goToFirst"
        @get-prev="getPrev"
        @go-to-last="goToLast"
        @jump-to-page="jumpToPage"
        @set-page-size-filter="setPageSizeFilter"
        :page_filter="approvalsFilters"
        :page="page"
        :numPages="num_pages"
        :isLoading="isLoading"
      >
        <template v-slot:message>
          <div class="flex items-center">
            <div class="pl-3 pt-1">
              {{ viewingFromNbr }}-{{ viewingToNbr }}<span v-if="count != null"> | {{ count }} Total Companies</span>
            </div>
            <base-button @click="generateCSV" class="underline ml-2">⬇</base-button>
          </div>
        </template>
      </base-pagination-header>

      <!--
        Table Component

        Custom <table> instead of BaseTableFull so we can pin column
        widths via <colgroup>. With table-auto on a hierarchical view
        like this, nested content kept shifting the outer columns
        around — table-fixed with explicit widths keeps every row
        (including the expansion rows below each company) aligned to
        the same grid.
      -->
      <div class="h-full overflow-y-auto border bg-white">
        <table class="w-full table-fixed text-left text-sm">
          <colgroup>
            <col style="width: 2.5rem" />
            <col style="width: 2.75rem" />
            <col style="width: 24%" />
            <col style="width: 28%" />
            <col style="width: 6rem" />
            <col />
          </colgroup>
          <thead class="bg-gray-100 sticky top-0 z-10">
            <tr>
              <th class="border border-gray-200 px-2 py-1.5 text-sm font-semibold align-middle text-center">
                <input
                  type="checkbox"
                  :checked="allSelectableOnPageSelected"
                  :indeterminate.prop="someSelectableOnPageSelected && !allSelectableOnPageSelected"
                  :disabled="!selectableGroupsOnPage.length"
                  @change="toggleSelectAllOnPage"
                  title="Select/deselect all groups on this page that have linked Companies"
                  class="cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                />
              </th>
              <th
                v-for="header in tableHeadersList"
                :key="header.value || header.orderFieldName"
                class="border border-gray-200 px-2 py-1.5 text-sm font-semibold align-middle"
              >
                <div class="flex items-center justify-between space-x-1">
                  <div>{{ header.value }}</div>
                  <div v-if="header.orderFieldName" class="text-xs">
                    <div
                      v-if="noOrdering(header.orderFieldName)"
                      @click="changeOrdering(header.orderFieldName)"
                      class="flex flex-col space-y-0 leading-none"
                    >
                      <div class="hover:cursor-pointer hover:text-accent-primary">▲</div>
                      <div class="hover:cursor-pointer hover:text-accent-primary">▼</div>
                    </div>
                    <div
                      v-else-if="orderedAscending(header.orderFieldName)"
                      @click="changeOrdering(header.orderFieldName)"
                      class="hover:cursor-pointer hover:text-accent-primary"
                    >▲</div>
                    <div
                      v-else-if="orderedDescending(header.orderFieldName)"
                      @click="changeOrdering(header.orderFieldName)"
                      class="hover:cursor-pointer hover:text-accent-primary"
                    >▼</div>
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <PMACompanyRow
              v-for="company in companies"
              :key="company.company_name"
              :company="company"
              :filterQuery="fetchFilter.value"
              :resetObject="resetFilters"
              :campaigns="campaigns"
              :is-loading="isLoading"
              :checked="selectedGroupNames.includes(company.company_name)"
              @set-loading="setLoading"
              @refresh-data="loadCompanies(fetchFilter.value, true)"
              @toggle-checked="toggleGroupChecked"
            ></PMACompanyRow>
          </tbody>
        </table>
      </div>
    </base-card>
  </div>
</template>

<script setup>
// imports

import { ref, computed, onMounted, onUnmounted, reactive, watch } from "vue";
import { useStore } from "vuex";
import {
  getPMACompanies,
  getCompanyNameFilterChoices,
  getPMAsForCSV,
} from "@/services/PMAList.js";
import { getActiveCampaigns } from "@/services/Campaign.js";
import { getMinimalCollectionTags } from "@/services/CollectionTag.js";
import { createCSV } from "@/composables/createCSV.js";
import PMACompanyRow from "./pma_page/table/PMACompanyRow.vue";
import PMAFilters from "./pma_page/PMAFilters.vue";
import PMABulkOperations from "./pma_page/PMABulkOperations.vue";

// constants/variables

const store = useStore();

const filterContainer = ref(null);
// Headers now describe the company-level row. The ▶/▼ expand control
// sits in the first column inside PMACompanyRow itself; no header
// needed for it. Ordering at this level uses `company_name` (server
// honors it via the existing OrderingFilter).
const tableHeadersList = ref([
  { value: "" },
  { value: "Company Name"},
  { value: "Location" },
  { value: "PMA Count" },
  { value: "Linked Companies" },
]);

const next = ref();
const prev = ref();
const count = ref();
const num_pages = ref();
const page_size = ref();
const page = ref(1);
const companies = ref([]);
const campaigns = ref([]);
const orderBy = ref([]);

const companyFilterOptions = ref([]);
const collectionTags = ref([]);
const isLoading = ref(false);
const changedApprovalsFilters = ref(false);

// Selection state for bulk operations. We track selected company groups by
// their pma company_name (the key the grouped view is built on) so the
// selection survives a page refetch even if row order shifts. The downstream
// list of unique linked Companies is derived from the selected groups.
const selectedGroupNames = ref([]);
const selectedCompaniesByName = reactive({});

const approvalsFilters = reactive({
  companyFilter: "",
  makeFilter: "",
  excludeMakeFilter: "",
  modelFilter: "",
  partNameFilter: "",
  supplementDateBeforeFilter: null,
  supplementDateAfterFilter: null,
  supplimentDateFilter: "",
  // Linked-Company filters: arrays of numeric IDs, serialized as
  // comma-separated values into the request URL by fetchFilter.
  collectionTagFilter: [],
  excludeCollectionTagFilter: [],
  campaignFilter: [],
  excludeCampaignFilter: [],
  pageSizeFilter: 50,
});
const isDropDownVisible = reactive({
  dropdownVisible: false,
  objectHasChanged: true,
});
const resetFilters = reactive({
  buttonHit: false,
});

// Options for the typeahead widgets. Mapped here once (rather than
// per-widget) so PMAFilters stays presentational.
const campaignOptions = computed(() => {
  return (campaigns.value || []).map((c) => ({
    label: c.campaign_name,
    value: c.id,
  }));
});

const collectionTagOptions = computed(() => {
  return (collectionTags.value || []).map((t) => ({
    label: t.collection_tag_name,
    value: t.id,
  }));
});

// Company filter is single-select; the loader returns a flat array of
// company_name strings with a "---------" sentinel at the head. We
// drop the sentinel and map to the typeahead's {label, value} shape.
const companyTypeAheadOptions = computed(() => {
  return (companyFilterOptions.value || [])
    .filter((name) => name && name !== "---------")
    .map((name) => ({ label: name, value: name }));
});

// Groups on the current page that actually have linked Companies — only
// these can be checked. Groups with no links can't drive bulk ops.
const selectableGroupsOnPage = computed(() => {
  return companies.value.filter((c) => (c.linked_companies || []).length > 0);
});

const allSelectableOnPageSelected = computed(() => {
  if (!selectableGroupsOnPage.value.length) return false;
  return selectableGroupsOnPage.value.every((c) =>
    selectedGroupNames.value.includes(c.company_name)
  );
});

const someSelectableOnPageSelected = computed(() => {
  return selectableGroupsOnPage.value.some((c) =>
    selectedGroupNames.value.includes(c.company_name)
  );
});

// Flat, deduplicated list of {id, company_name} across all selected groups.
// CompanyBulkOperations only needs each entry to have an .id field — the
// existing bulk endpoints take company_ids and ignore the rest.
const selectedCompanies = computed(() => {
  const byId = new Map();
  for (const name of selectedGroupNames.value) {
    const linked = selectedCompaniesByName[name] || [];
    for (const lc of linked) {
      if (lc && lc.id != null && !byId.has(lc.id)) {
        byId.set(lc.id, lc);
      }
    }
  }
  return Array.from(byId.values());
});

// computed values

const viewingFromNbr = computed(function () {
  return (
    page.value * approvalsFilters.pageSizeFilter +
    1 -
    approvalsFilters.pageSizeFilter
  );
});

const viewingToNbr = computed(function () {
  // When the backend returns no total (unfiltered, fast path), we
  // detect "last page" by the absence of a `next` link and derive
  // the upper bound from the rows actually received. With a total,
  // use the count on the final page so the display is exact.
  if (count.value != null && page.value === num_pages.value) {
    return count.value;
  }
  if (next.value == null && companies.value.length < approvalsFilters.pageSizeFilter) {
    return (page.value - 1) * approvalsFilters.pageSizeFilter + companies.value.length;
  }
  return page.value * approvalsFilters.pageSizeFilter;
});

// fetchFilter is the canonical filter query string used by all three
// PMA endpoints — companies list at the top level, makes when a
// company expands, and PMAs when a make expands. Passing it down lets
// every level honor the active filters.
const fetchFilter = computed(function () {
  let query = new URLSearchParams({});
  if (approvalsFilters.supplimentDateFilter) {
    query.append("date", approvalsFilters.supplimentDateFilter);
  }
  if (approvalsFilters.companyFilter) {
    query.append("company_name", approvalsFilters.companyFilter);
  }
  if (approvalsFilters.makeFilter) {
    query.append("make", approvalsFilters.makeFilter);
  }
  if (approvalsFilters.excludeMakeFilter) {
    query.append("exclude_make", approvalsFilters.excludeMakeFilter);
  }
  if (approvalsFilters.modelFilter) {
    query.append("model", approvalsFilters.modelFilter);
  }
  if (approvalsFilters.partNameFilter) {
    query.append("part_name", approvalsFilters.partNameFilter);
  }
  if (approvalsFilters.pageSizeFilter) {
    query.append("page_size", approvalsFilters.pageSizeFilter);
  }
  if (approvalsFilters.supplementDateAfterFilter) {
    query.append("supplement_date_after", approvalsFilters.supplementDateAfterFilter);
  }
  if (approvalsFilters.supplementDateBeforeFilter) {
    query.append("supplement_date_before", approvalsFilters.supplementDateBeforeFilter);
  }
  if (approvalsFilters.collectionTagFilter.length > 0) {
    query.append("collection_tag", approvalsFilters.collectionTagFilter.join(","));
  }
  if (approvalsFilters.excludeCollectionTagFilter.length > 0) {
    query.append("exclude_collection_tag", approvalsFilters.excludeCollectionTagFilter.join(","));
  }
  if (approvalsFilters.campaignFilter.length > 0) {
    query.append("campaign", approvalsFilters.campaignFilter.join(","));
  }
  if (approvalsFilters.excludeCampaignFilter.length > 0) {
    query.append("exclude_campaign", approvalsFilters.excludeCampaignFilter.join(","));
  }
  if (orderBy.value.length > 0) {
    query.append("order_by", orderBy.value.join(","));
  }
  let url;
  let queriesCount = 0;
  for (let value of query.values()) {
    queriesCount = queriesCount + 1;
  }
  if (queriesCount > 0) {
    url = "?" + query.toString();
  } else {
    url = "?";
  }
  return url;
});

// async functions

async function loadCompanies(endpoint = fetchFilter.value, retainSpot) {
  isLoading.value = true;
  // Bumping resetFilters.buttonHit signals descendant rows to collapse
  // any open expansions — counts at every level have likely changed.
  resetFilters.buttonHit = !resetFilters.buttonHit;
  let payload = {
    token: store.state.auth.token,
    endpoint: retainSpot ? endpoint + `&page=${page.value}` : endpoint,
  };
  const response = await getPMACompanies(payload);
  next.value = response.next ? `&page=${response.page + 1}` : null;
  prev.value = response.prev ? `&page=${response.page - 1}` : null;
  count.value = response.count;
  page.value = response.page;
  num_pages.value = response.num_pages;
  companies.value = response.companies;
  page_size.value = response.page_size;
  isLoading.value = false;
}

async function setApprovalFilters(updatedFilters) {
  approvalsFilters.companyFilter = updatedFilters.value.companyFilter.value;
  approvalsFilters.makeFilter = updatedFilters.value.makeFilter.value;
  approvalsFilters.excludeMakeFilter = updatedFilters.value.excludeMakeFilter.value;
  approvalsFilters.modelFilter = updatedFilters.value.modelFilter.value;
  approvalsFilters.partNameFilter = updatedFilters.value.partNameFilter.value;
  approvalsFilters.supplementDateAfterFilter = updatedFilters.value.supplementDateAfterFilter.value;
  approvalsFilters.supplementDateBeforeFilter = updatedFilters.value.supplementDateBeforeFilter.value;
  approvalsFilters.collectionTagFilter = updatedFilters.value.collectionTagFilter.value || [];
  approvalsFilters.excludeCollectionTagFilter = updatedFilters.value.excludeCollectionTagFilter.value || [];
  approvalsFilters.campaignFilter = updatedFilters.value.campaignFilter.value || [];
  approvalsFilters.excludeCampaignFilter = updatedFilters.value.excludeCampaignFilter.value || [];
  await loadCompanies(fetchFilter.value);
}

async function setPageSizeFilter(target) {
  target == "clear"
    ? (approvalsFilters.pageSizeFilter = 50)
    : (approvalsFilters.pageSizeFilter = target);
  await loadCompanies(fetchFilter.value);
}

async function setCompanyFilterOptions() {
  let payload = {
    token: store.state.auth.token,
    endpoint: "",
  };
  const result = await getCompanyNameFilterChoices(payload);
  companyFilterOptions.value = ["---------"];
  result.company_names.forEach((company) => {
    companyFilterOptions.value.push(company);
  });
}

function orderedAscending(fieldName) {
  return orderBy.value.includes(fieldName);
}

function orderedDescending(fieldName) {
  return orderBy.value.includes("-" + fieldName);
}

function noOrdering(fieldName) {
  return !orderBy.value.includes(fieldName) && !orderBy.value.includes("-" + fieldName);
}

function changeOrdering(fieldName) {
  if (orderedAscending(fieldName)) {
    const index = orderBy.value.indexOf(fieldName);
    orderBy.value.splice(index, 1);
  } else if (orderedDescending(fieldName)) {
    const index = orderBy.value.indexOf("-" + fieldName);
    orderBy.value[index] = fieldName;
  } else {
    orderBy.value.push("-" + fieldName);
  }
}

const awaitingSearch = ref(false);

watch(
  () => orderBy,
  () => {
    if (!awaitingSearch.value) {
      setTimeout(() => {
        loadCompanies(fetchFilter.value);
        awaitingSearch.value = false;
      }, 1000);
    }
    awaitingSearch.value = true;
  },
  { deep: true }
);

// functions

function getNext() {
  if (next.value) {
    loadCompanies(fetchFilter.value + next.value);
  } else {
    alert("No next page");
  }
}

function getPrev() {
  if (prev.value) {
    loadCompanies(fetchFilter.value + prev.value);
  } else {
    alert("No previous page");
  }
}

function goToFirst() {
  if (page.value != 1) {
    loadCompanies(fetchFilter.value + `&page=${1}`);
  } else {
    alert("At first page");
  }
}

function goToLast() {
  // The "Last" arrow is hidden in BasePaginationArrows when num_pages
  // is null (unfiltered, total skipped). Guard anyway in case the
  // handler is triggered programmatically.
  if (num_pages.value == null) {
    return;
  }
  if (num_pages.value != page.value) {
    loadCompanies(fetchFilter.value + `&page=${num_pages.value}`);
  } else {
    alert("At last page");
  }
}

function jumpToPage(targetPage) {
  loadCompanies(fetchFilter.value + `&page=${targetPage}`);
}

function setChangedApprovalsFilters() {
  changedApprovalsFilters.value = true;
}

function handleClickOutside(event) {
  if (filterContainer.value && filterContainer.value.contains(event.target)) {
    isDropDownVisible.dropdownVisible = false;
    isDropDownVisible.objectHasChanged = !isDropDownVisible.objectHasChanged;
  }
}

// CSV export still hits the legacy flat endpoint (one row per PMA) so
// users get an exhaustive sheet regardless of how the table groups.
async function generateCSV() {
  isLoading.value = true;
  const token = store.state.auth.token;
  // count.value is now distinct companies, not PMAs, so the 150k cap
  // is much less likely to bite. Leave the safety check in place.
  if (count.value > 150000) {
    alert("Cannot generate a CSV for more than 150,000 records. Please contact the Tech Team if you need further assistance.");
    isLoading.value = false;
    return;
  } else {
    const fields = [
      "company_name",
      "status",
      "cfr_part_reference",
      "pma_holder_number",
      "office_of_primary_responsibility",
      "supplement_number",
      "raw_supplement_date",
      "supplement_date",
      "part_name",
      "approved_replacement_for_part_number",
      "make",
      "model",
      "faa_approval_basis",
      "address",
      "city",
      "state",
      "zip",
      "country",
      "cfr_subpart_appendix_reference",
      "cfr_section_reference",
    ];
    const response = await getPMAsForCSV(fetchFilter.value, token);
    if (response.status == 200) {
      createCSV(fields, response.data.pmas, "pma");
      isLoading.value = false;
    }
  }
}

async function loadCampaigns() {
  const token = store.state.auth.token;
  const result = await getActiveCampaigns(token);
  campaigns.value = result;
}

async function loadCollectionTags() {
  const token = store.state.auth.token;
  const result = await getMinimalCollectionTags(token);
  collectionTags.value = Array.isArray(result) ? result : [];
}

// life-cycle

onMounted(() => {
  loadCompanies();
  setCompanyFilterOptions();
  loadCampaigns();
  loadCollectionTags();
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

function setLoading(loadingStatus) {
  isLoading.value = loadingStatus;
}

function toggleGroupChecked(companyName) {
  const idx = selectedGroupNames.value.indexOf(companyName);
  if (idx >= 0) {
    selectedGroupNames.value.splice(idx, 1);
    delete selectedCompaniesByName[companyName];
  } else {
    const group = companies.value.find((c) => c.company_name === companyName);
    if (!group || !(group.linked_companies || []).length) return;
    selectedGroupNames.value.push(companyName);
    selectedCompaniesByName[companyName] = group.linked_companies;
  }
}

function toggleSelectAllOnPage() {
  if (allSelectableOnPageSelected.value) {
    // Deselect everything on this page (preserve selections from other pages).
    for (const c of selectableGroupsOnPage.value) {
      const idx = selectedGroupNames.value.indexOf(c.company_name);
      if (idx >= 0) {
        selectedGroupNames.value.splice(idx, 1);
        delete selectedCompaniesByName[c.company_name];
      }
    }
  } else {
    for (const c of selectableGroupsOnPage.value) {
      if (!selectedGroupNames.value.includes(c.company_name)) {
        selectedGroupNames.value.push(c.company_name);
        selectedCompaniesByName[c.company_name] = c.linked_companies;
      }
    }
  }
}

function clearSelectedCompanies() {
  selectedGroupNames.value.splice(0);
  for (const key of Object.keys(selectedCompaniesByName)) {
    delete selectedCompaniesByName[key];
  }
}
</script>
